import * as THREE from 'three';
import {
  ABILITIES,
  CHARACTER,
  DEFAULT_MODIFIERS,
  EMOTES,
  FIXED_DT,
  FX,
  NET,
  PALETTE,
  RANDOM_EVENT_INFO,
  cloneCharacterState,
  createCharacterState,
  dist2D,
  stepCharacter,
  type CharacterState,
  type ChatMessage,
  type Hazard,
  type JoinedRoom,
  type MoveInput,
  type PlayerSnap,
  type RoomState,
  type Snapshot,
  type StepModifiers,
  type WorldEvent,
  type WorldModifiers,
} from '@kokun/shared';
import { Avatar, type AnimState } from '../characters/avatar';
import { makeBubble, makeNametag } from '../characters/nametag';
import { faceCache } from '../characters/face-cache';
import { makeCell } from './objects-view';
import type { World3D } from './world3d';
import { InterpolationBuffer } from '../../multiplayer/interpolation';
import { connection } from '../../multiplayer/connection';
import { audio, type Sfx } from '../audio/audio';
import { floatText, live, ui, type Waypoint } from '../../app/game-store';
import { settings } from '../../app/settings';
import { toast } from '../../ui/dom';

interface Pending {
  input: MoveInput;
  t: number;
}

interface Remote {
  id: string;
  avatar: Avatar;
  buf: InterpolationBuffer;
  tag: THREE.Sprite | null;
  tagKey: string;
  bubble: { sprite: THREE.Sprite; until: number } | null;
  wasGrounded: boolean;
  faceKey: string;
  pos: THREE.Vector3;
}

const NAME_OFFSET = 2.7;

/**
 * Sesión de juego en el cliente.
 *  - Predicción local con la MISMA física que el servidor (paso fijo de 1/30 s).
 *  - Reconciliación al recibir el estado autoritativo: se reaplican los inputs pendientes y el
 *    error residual se suaviza visualmente.
 *  - Jugadores remotos interpolados en el pasado (NET.INTERPOLATION_DELAY_MS).
 *  - El renderizado va a la frecuencia del monitor, independiente de la red.
 */
export class GameSession {
  readonly mode: 'online' | 'offline';
  private you: string;
  private state: CharacterState;
  private prev: CharacterState;
  private pending: Pending[] = [];
  private seq = 0;
  private acc = 0;
  private visualOffset = new THREE.Vector3();
  private local: Remote;
  private remotes = new Map<string, Remote>();
  private room: RoomState | null = null;
  private hazards: Hazard[] = [];
  private modifiers: WorldModifiers | null = null;
  private myFx = 0;
  private myEmote: string | undefined;
  private stepTimer = 0;
  private offs: Array<() => void> = [];
  private lastPrompt: string | null = null;
  private celebrateUntil = 0;
  private renderPos = new THREE.Vector3();

  constructor(
    private w: World3D,
    joined: JoinedRoom | null,
    private localCharacter: RoomState['players'][number]['character'],
    localId: string,
  ) {
    this.mode = joined ? 'online' : 'offline';
    this.you = localId;
    const spawn = w.map.spawnPoints[0];
    this.state = createCharacterState(spawn[0], spawn[1], spawn[2]);
    this.prev = cloneCharacterState(this.state);
    this.local = this.makeRemote(localId, localCharacter);
    w.camera.snap();
    w.input.enabled = true;

    this.offs.push(w.engine.onFrame((dt) => this.frame(dt)));
    this.offs.push(w.input.on('press', (a) => this.onAction(a)));
    this.offs.push(w.input.on('emote', (n) => this.emote(n)));
    this.offs.push(w.input.on('wheel', (d) => w.camera.zoom(d)));
    live.project = (x, y, z) => {
      const v = new THREE.Vector3(x, y, z).project(w.engine.camera);
      return { x: (v.x * 0.5 + 0.5) * window.innerWidth, y: (-v.y * 0.5 + 0.5) * window.innerHeight, behind: v.z > 1 };
    };

    if (joined) this.applyJoined(joined);
    else {
      w.serverNow = () => Date.now();
      w.objects.applyObjects([], true);
      ui.set({ mode: 'offline', room: null, mission: null, minigame: null, you: localId });
      audio.intensity = 'calm';
    }
  }

  // ------------------------------------------------------------------ red

  private applyJoined(j: JoinedRoom) {
    this.you = j.you;
    this.room = j.room;
    this.w.serverNow = () => connection.serverNow();
    this.w.objects.applyObjects(j.world.objects, true);
    this.w.objects.applyCrates(j.world.crates);
    this.hazards = j.world.hazards;
    this.w.objects.applyHazards(j.world.hazards);
    this.w.setVanished(j.world.vanished);
    this.applyModifiers(j.world.modifiers);
    this.w.objects.setCoins(j.minigame?.coins ?? []);
    this.pending = [];
    ui.set({ mode: 'online', room: j.room, you: j.you, mission: j.mission, minigame: j.minigame, chat: j.chat, results: null });
    this.syncRoster(j.room);
    this.bindSocket();
    audio.intensity = j.mission ? 'mission' : 'calm';
  }

  private bindSocket() {
    const s = connection.socket;
    if (!s) return;
    const on = <A extends unknown[]>(ev: string, fn: (...a: A) => void) => {
      (s.on as (e: string, f: (...a: A) => void) => void)(ev, fn);
      this.offs.push(() => (s.off as (e: string, f: (...a: A) => void) => void)(ev, fn));
    };
    on('snapshot', (snap: Snapshot) => this.onSnapshot(snap));
    on('room:state', (r: RoomState) => {
      const prevPhase = this.room?.phase;
      this.room = r;
      ui.set({ room: r });
      this.syncRoster(r);
      if (r.phase !== prevPhase) this.onPhase(r.phase, prevPhase);
    });
    on('room:joined', (j: JoinedRoom) => {
      this.applyJoined(j);
      toast('Reconectado a la sala ✔', 'success');
    });
    on('world:objects', (p: { objects: Parameters<World3D['objects']['applyObjects']>[0] }) => {
      this.w.objects.applyObjects(p.objects, p.objects.some((o) => o.kind === 'display') && p.objects.length > 4);
      ui.set({ carrying: [...this.w.objects.carriers.values()].includes(this.you) });
    });
    on('world:hazards', (p: { hazards: Hazard[] }) => {
      this.hazards = p.hazards;
      this.w.objects.applyHazards(p.hazards);
    });
    on('world:modifiers', (m: WorldModifiers) => this.applyModifiers(m));
    on('world:vanished', (p: { ids: string[] }) => this.w.setVanished(p.ids));
    on('world:event', (e: WorldEvent) => this.onWorldEvent(e));
    on('mission:state', (m: JoinedRoom['mission']) => {
      const prev = ui.get().mission;
      ui.set({ mission: m });
      if (m && prev?.status === 'active' && m.status !== 'active') this.onMissionEnd(m.status === 'success');
    });
    on('minigame:state', (m: JoinedRoom['minigame']) => {
      ui.set({ minigame: m });
      this.w.objects.setCoins(m?.coins ?? []);
    });
    on('minigame:coins', (p: { removed: string[]; added: JoinedRoom['world']['objects'] & never; scores: Record<string, number> }) => {
      const removedAt = this.w.objects.removeCoins(p.removed);
      removedAt.forEach((pos) => {
        this.w.particles.burst(pos, { count: 10, colors: [0xffd60a, 0xfff3b0], speed: 2, up: 3, life: 0.6 });
        if (this.renderPos.distanceTo(new THREE.Vector3(...pos)) < 3) audio.play('coin');
      });
      this.w.objects.addCoins(p.added as never);
      const mg = ui.get().minigame;
      if (mg) ui.set({ minigame: { ...mg, scores: p.scores } });
    });
    on('match:results', (r: NonNullable<ReturnType<typeof ui.get>['results']>) => ui.set({ results: r }));
    on('ability:cooldown', (p: { ability: keyof typeof ABILITIES; readyAt: number }) =>
      ui.set((u) => ({ cooldowns: { ...u.cooldowns, [p.ability]: p.readyAt } })),
    );
    on('chat:message', (m: ChatMessage) => this.onChat(m));
    on('notify', (n: { level: 'info' | 'success' | 'warn' | 'error'; text: string }) => toast(n.text, n.level));
  }

  private onSnapshot(snap: Snapshot) {
    for (const p of snap.players) {
      if (p.id === this.you) {
        this.myFx = p.fx;
        this.myEmote = p.e;
        this.local.avatar.bigHead = !!(p.fx & FX.BIGHEAD);
        this.local.avatar.setPaint(p.fx & FX.PAINT ? PALETTE[p.pc ?? 0] : null);
        continue;
      }
      const r = this.remotes.get(p.id);
      r?.buf.push(snap.t, p);
    }
    if (snap.crates) this.w.objects.applyCrates(snap.crates);
    if (snap.me) this.reconcile(snap.me.seq, snap.me.state);
  }

  private reconcile(ackSeq: number, server: CharacterState) {
    this.pending = this.pending.filter((p) => p.input.seq > ackSeq);
    const before = new THREE.Vector3(this.state.x, this.state.y, this.state.z);
    const s = cloneCharacterState(server);
    const mods = this.mods();
    for (const p of this.pending) {
      this.w.physics.setTime(p.t);
      stepCharacter(s, p.input, this.w.physics, mods, FIXED_DT);
    }
    const err = before.clone().sub(new THREE.Vector3(s.x, s.y, s.z));
    this.state = s;
    if (err.length() > 4) {
      this.visualOffset.set(0, 0, 0);
      this.prev = cloneCharacterState(s);
    } else if (err.length() > 0.01) this.visualOffset.add(err);
  }

  /** Sincroniza los avatares con la lista de jugadores de la sala. */
  private syncRoster(r: RoomState) {
    const ids = new Set(r.players.map((p) => p.id));
    for (const [id, rem] of this.remotes) {
      if (!ids.has(id)) {
        this.w.dynamic.remove(rem.avatar.root);
        rem.avatar.dispose();
        this.remotes.delete(id);
        faceCache.drop(id);
      }
    }
    const blocked = new Set(ui.get().profile?.blocked ?? []);
    for (const p of r.players) {
      const isMe = p.id === this.you;
      const rem = isMe ? this.local : (this.remotes.get(p.id) ?? this.makeRemote(p.id, p.character));
      if (!isMe && !this.remotes.has(p.id)) this.remotes.set(p.id, rem);
      if (JSON.stringify(rem.avatar.config) !== JSON.stringify(p.character)) rem.avatar.build(p.character);
      const tagKey = `${p.name}|${p.level}|${p.isHost}`;
      if (tagKey !== rem.tagKey) {
        if (rem.tag) rem.avatar.root.remove(rem.tag);
        rem.tag = makeNametag(p.name, p.tag, p.level, { host: p.isHost, me: isMe });
        rem.tag.position.y = NAME_OFFSET;
        rem.avatar.root.add(rem.tag);
        rem.tagKey = tagKey;
      }
      rem.avatar.setGhost(!p.connected);
      const showFace = p.character.useFace && p.faceRev > 0 && (isMe || (settings.get().showOthersFaces && !blocked.has(p.id)));
      const faceKey = showFace ? `${p.faceRev}` : '';
      if (faceKey !== rem.faceKey) {
        rem.faceKey = faceKey;
        if (!showFace) rem.avatar.setFaceImage(null);
        else void faceCache.get(p.id, p.faceRev).then((img) => rem.faceKey === faceKey && rem.avatar.setFaceImage(img));
      }
    }
  }

  private makeRemote(id: string, character: RoomState['players'][number]['character']): Remote {
    const avatar = new Avatar(character);
    this.w.dynamic.add(avatar.root);
    return { id, avatar, buf: new InterpolationBuffer(), tag: null, tagKey: '', bubble: null, wasGrounded: true, faceKey: '', pos: new THREE.Vector3() };
  }

  private applyModifiers(m: WorldModifiers) {
    const prev = this.modifiers;
    this.modifiers = m;
    ui.set({ modifiers: m });
    this.w.setBlackout(m.blackout);
    this.w.setAlarm(m.alarm);
    if (m.alarm && !prev?.alarm) audio.play('alarm');
    if (m.label && m.label !== prev?.label) audio.play('beep');
  }

  private mods(): StepModifiers {
    const m = this.modifiers;
    const carrying = !!(this.myFx & FX.CARRYING);
    return {
      ...DEFAULT_MODIFIERS,
      gravityScale: m?.gravityScale ?? 1,
      windX: m?.wind[0] ?? 0,
      windZ: m?.wind[1] ?? 0,
      slippery: this.hazards,
      speedMult: carrying ? CHARACTER.CARRY_SPEED_MULT : 1,
      canSprint: !carrying,
    };
  }

  // ------------------------------------------------------------------ fases y eventos

  private onPhase(phase: RoomState['phase'], prev?: RoomState['phase']) {
    if (phase === 'countdown') {
      ui.set({ results: null });
      for (let i = 0; i < 4; i++) setTimeout(() => audio.play(i === 3 ? 'go' : 'beep'), i * 1000);
    }
    if (phase === 'mission') {
      audio.intensity = 'mission';
      this.pending = [];
    }
    if (phase === 'minigame') toast('🪙 ¡Lluvia de Kokuns! Recoge todas las monedas que puedas.', 'info', 5000);
    if (phase === 'lobby') {
      audio.intensity = 'calm';
      ui.set({ mission: null, minigame: null, cooldowns: {} });
      if (prev === 'results') ui.set({ results: null });
    }
  }

  private onMissionEnd(success: boolean) {
    audio.play(success ? 'victory' : 'defeat');
    if (success) {
      this.celebrateUntil = performance.now() + 4000;
      this.w.particles.burst([0, 4, -20], { count: 120, speed: 8, up: 10, life: 2.5 });
      this.w.particles.burst(this.renderPos.clone().add(new THREE.Vector3(0, 2, 0)), { count: 60, speed: 5, up: 8, life: 2 });
    }
  }

  private posOf(id: string | undefined): THREE.Vector3 | null {
    if (!id) return null;
    if (id === this.you) return this.renderPos.clone();
    return this.remotes.get(id)?.pos.clone() ?? null;
  }

  private nameOf(id: string | undefined) {
    return this.room?.players.find((p) => p.id === id)?.name ?? 'Alguien';
  }

  private sfxAt(s: Sfx, pos: THREE.Vector3 | null) {
    const d = pos ? pos.distanceTo(this.renderPos) : 0;
    if (d < 40) audio.play(s, Math.max(0.15, 1 - d / 40));
  }

  private onWorldEvent(e: WorldEvent) {
    const at = e.pos ? new THREE.Vector3(...e.pos) : null;
    const meTarget = e.target === this.you || e.targets?.includes(this.you);
    switch (e.type) {
      case 'push': {
        if (!e.target) {
          this.sfxAt('whoosh', at);
          break;
        }
        this.sfxAt('push', at);
        if (at) {
          this.w.particles.burst(at.clone().setY(at.y + 1), { count: 18, colors: [0xffffff, 0xffd60a], speed: 4, up: 3, life: 0.6 });
          floatText(at.x, at.y + 2.4, at.z, '¡PUM!', '#ffd60a');
        }
        if (meTarget) {
          this.w.camera.addShake(0.8);
          toast(`🫸 ¡${this.nameOf(e.actor)} te ha empujado! Te recuperas en un momento.`, 'warn');
        }
        break;
      }
      case 'blocked':
        this.sfxAt('blocked', at);
        if (at) floatText(at.x, at.y + 2.4, at.z, '🛡️ protegido', '#a0c4ff');
        break;
      case 'slip':
        this.sfxAt('slip', at);
        if (at) this.w.particles.burst(at, { count: 14, colors: [0xffffff, 0xbde0fe], speed: 3, up: 2, life: 0.8 });
        if (meTarget) toast('🍌 ¡Has resbalado en un charco de jabón!', 'warn');
        break;
      case 'banana':
        this.sfxAt('banana', at);
        break;
      case 'paint': {
        this.sfxAt('paint', at);
        const color = PALETTE[e.value ?? 0];
        if (at) this.w.particles.burst(at.clone().setY(at.y + 0.5), { count: 50, colors: [color, 0xffffff], speed: 6, up: 5, life: 1.2, size: 0.18 });
        if (meTarget) {
          ui.set({ paintedUntil: performance.now() + ABILITIES.paint.duration * 1000 });
          toast(`🎨 ¡${this.nameOf(e.actor)} te ha pintado!`, 'warn');
        }
        break;
      }
      case 'bighead':
        this.sfxAt('inflate', at);
        if (meTarget) toast(`🎈 ¡${this.nameOf(e.actor)} te ha inflado la cabeza! Dura ${ABILITIES.bighead.duration} s.`, 'warn');
        break;
      case 'teleport': {
        this.sfxAt('teleport', at);
        if (at) this.w.particles.burst(at.clone().setY(at.y + 1), { count: 30, colors: [0xc77dff, 0xffffff], speed: 3, up: 4, life: 1 });
        if (meTarget) {
          this.w.camera.snap();
          this.pending = [];
          toast('🌀 ¡Teletransporte sorpresa!', 'info');
        }
        break;
      }
      case 'pickup':
        this.sfxAt('pickup', at);
        break;
      case 'drop':
        this.sfxAt('drop', at);
        break;
      case 'plate':
        this.sfxAt('plate', at);
        break;
      case 'stage':
        audio.play('stage');
        toast(`✅ Objetivo completado: ${e.text}`, 'success', 4500);
        this.w.particles.burst(this.renderPos.clone().setY(this.renderPos.y + 2.5), { count: 40, speed: 4, up: 6, life: 1.5 });
        break;
      case 'redButton':
        this.sfxAt('button', at);
        toast(`🔴 ${this.nameOf(e.actor)} ha pulsado el botón rojo: ${e.text}`, 'info', 4500);
        break;
      case 'vanish':
        this.sfxAt('whoosh', at);
        toast(`🫥 ${this.nameOf(e.actor)} ha hecho desaparecer el puente durante unos segundos`, 'info');
        break;
    }
    if (e.type === 'deliver' || (e.type === 'drop' && false)) this.sfxAt('deliver', at);
  }

  private onChat(m: ChatMessage) {
    ui.set((u) => ({ chat: [...u.chat.slice(-60), m] }));
    if (m.system) return;
    audio.play('chat', 0.6);
    const r = m.from === this.you ? this.local : this.remotes.get(m.from);
    if (!r) return;
    if (r.bubble) r.avatar.root.remove(r.bubble.sprite);
    const sprite = makeBubble(m.text);
    sprite.position.y = NAME_OFFSET + 0.8;
    r.avatar.root.add(sprite);
    r.bubble = { sprite, until: performance.now() + 4500 };
  }

  // ------------------------------------------------------------------ acciones

  private onAction(a: string) {
    if (ui.get().paused) return;
    if (a === 'interact') {
      if (this.mode === 'online') connection.emit('act:interact');
    } else if (a === 'prank') this.useAbility('push');
    else if (a === 'special') {
      const me = this.room?.players.find((p) => p.id === this.you);
      if (me) this.useAbility(me.special);
    }
  }

  useAbility(ability: keyof typeof ABILITIES) {
    if (this.mode !== 'online') return toast('Las bromas necesitan conexión con otros jugadores.', 'info');
    const ready = ui.get().cooldowns[ability] ?? 0;
    if (connection.serverNow() < ready) return audio.play('error', 0.5);
    connection.emit('act:ability', { ability, aimYaw: this.w.camera.yaw });
  }

  emote(n: number) {
    const e = EMOTES[n - 1];
    if (!e) return;
    const level = ui.get().profile?.player.level ?? 1;
    if (e.unlockLevel > level) return toast(`${e.icon} ${e.name}: se desbloquea en el nivel ${e.unlockLevel}`, 'info');
    if (this.mode === 'online') connection.emit('act:emote', { emote: e.id });
    else {
      this.myEmote = e.id;
      setTimeout(() => (this.myEmote = undefined), e.duration * 1000);
    }
  }

  interactMobile() {
    this.onAction('interact');
  }

  // ------------------------------------------------------------------ bucle

  private stepLocal() {
    const inp = this.w.input;
    const { mx, mz, sprint } = inp.axes();
    const input: MoveInput = { seq: ++this.seq, mx, mz, yaw: this.w.camera.yaw, jump: inp.consumeJump(), sprint };
    const t = this.w.serverNow() / 1000;
    this.w.physics.setTime(t);
    this.prev = cloneCharacterState(this.state);
    const res = stepCharacter(this.state, input, this.w.physics, this.mods(), FIXED_DT);
    if (this.mode === 'online') {
      this.pending.push({ input, t });
      if (this.pending.length > 90) this.pending.shift();
      connection.emit('input', { inputs: [input] });
    } else if (this.state.y < this.w.map.killY) {
      Object.assign(this.state, { x: 0, y: 1, z: 8, vx: 0, vy: 0, vz: 0 });
      this.prev = cloneCharacterState(this.state);
    }
    if (res.jumped) audio.play('jump', 0.7);
    if (res.launched) audio.play('pad');
    if (res.landed && res.landingSpeed > 6) {
      audio.play('land', Math.min(1, res.landingSpeed / 20));
      this.local.avatar.land(res.landingSpeed);
      this.w.particles.burst([this.state.x, this.state.y + 0.05, this.state.z], { count: 8, colors: [0xe9ecef, 0xced4da], speed: 2, up: 1, life: 0.5, gravity: 4 });
    }
    const hs = Math.hypot(this.state.vx, this.state.vz);
    if (this.state.grounded && hs > 1) {
      this.stepTimer -= FIXED_DT * hs;
      if (this.stepTimer <= 0) {
        audio.play('step', 0.5);
        this.stepTimer = 2.2;
      }
    }
  }

  private animFor(grounded: boolean, vy: number, hs: number, fx: number, emote: string | undefined, celebrate: boolean): AnimState {
    if (fx & FX.STUNNED) return 'stunned';
    if (!grounded) return vy > 0 ? 'jump' : 'fall';
    if (emote) return `emote:${emote}`;
    if (celebrate) return 'celebrate';
    if (hs > 6.5) return 'run';
    if (hs > 0.6) return 'walk';
    return 'idle';
  }

  private frame(dt: number) {
    if (ui.get().paused || ui.get().screen !== 'game') {
      this.w.input.takeLook();
    }
    const [lx, ly] = this.w.input.takeLook();
    this.w.camera.look(lx, ly);

    this.acc += dt;
    let steps = 0;
    while (this.acc >= FIXED_DT && steps < 8) {
      this.stepLocal();
      this.acc -= FIXED_DT;
      steps++;
    }
    if (steps === 8) this.acc = 0;
    const alpha = this.acc / FIXED_DT;

    // Local: interpolación entre pasos + corrección suavizada
    this.visualOffset.multiplyScalar(Math.exp(-dt * 10));
    const p = this.renderPos.set(
      this.prev.x + (this.state.x - this.prev.x) * alpha,
      this.prev.y + (this.state.y - this.prev.y) * alpha,
      this.prev.z + (this.state.z - this.prev.z) * alpha,
    );
    p.add(this.visualOffset);
    const la = this.local.avatar;
    la.root.position.copy(p);
    la.root.rotation.y = this.state.yaw;
    const celebrate = performance.now() < this.celebrateUntil;
    la.state = this.animFor(this.state.grounded, this.state.vy, Math.hypot(this.state.vx, this.state.vz), this.myFx | (this.state.stun > 0 ? FX.STUNNED : 0), this.myEmote, celebrate);
    la.speed = Math.hypot(this.state.vx, this.state.vz);
    this.syncCarry(this.local, !!(this.myFx & FX.CARRYING));
    la.update(dt);
    this.local.pos.copy(p);
    this.w.camera.update(p, dt);
    this.w.engine.shadowTarget = p;
    this.w.attachFlashlight(p);

    // Remotos
    const renderT = this.w.serverNow() - NET.INTERPOLATION_DELAY_MS;
    const camPos = this.w.engine.camera.position;
    for (const r of this.remotes.values()) {
      const s = r.buf.sample(renderT);
      if (!s) {
        r.avatar.root.visible = false;
        continue;
      }
      r.avatar.root.visible = true;
      r.avatar.root.position.set(s.x, s.y, s.z);
      r.avatar.root.rotation.y = s.yaw;
      r.pos.set(s.x, s.y, s.z);
      const snap: PlayerSnap = s.snap;
      const grounded = snap.g === 1;
      if (grounded && !r.wasGrounded && snap.vy < -6) r.avatar.land(-snap.vy);
      r.wasGrounded = grounded;
      r.avatar.state = this.animFor(grounded, snap.vy, snap.hs, snap.fx, snap.e, celebrate);
      r.avatar.speed = snap.hs;
      r.avatar.bigHead = !!(snap.fx & FX.BIGHEAD);
      r.avatar.setPaint(snap.fx & FX.PAINT ? PALETTE[snap.pc ?? 0] : null);
      this.syncCarry(r, !!(snap.fx & FX.CARRYING));
      if (r.tag) r.tag.visible = !(snap.fx & FX.HIDDEN) && camPos.distanceTo(r.pos) < 45;
      r.avatar.update(dt);
    }
    const now = performance.now();
    for (const r of [this.local, ...this.remotes.values()]) {
      if (r.bubble && now > r.bubble.until) {
        r.avatar.root.remove(r.bubble.sprite);
        r.bubble = null;
      }
    }
    if (this.modifiers?.confetti && Math.random() < 0.5) this.w.particles.rain(p, 12, 3);

    this.updatePrompt();
    this.updateWaypoints();
    live.fps = this.w.engine.fps;
  }

  private carryMeshes = new Map<string, THREE.Object3D>();
  private syncCarry(r: Remote, carrying: boolean) {
    const has = this.carryMeshes.has(r.id);
    if (carrying && !has) {
      const cell = makeCell();
      this.carryMeshes.set(r.id, cell);
      r.avatar.setCarry(cell);
    } else if (!carrying && has) {
      this.carryMeshes.delete(r.id);
      r.avatar.setCarry(null);
    }
  }

  /** Calcula el aviso de interacción (E) con la misma lógica de proximidad que el servidor. */
  private updatePrompt() {
    let prompt: string | null = null;
    const s = this.state;
    const carrying = !!(this.myFx & FX.CARRYING);
    if (this.mode === 'online') {
      let best = Infinity;
      const consider = (d: number, text: string) => {
        if (d < best) {
          best = d;
          prompt = text;
        }
      };
      if (!carrying)
        for (const it of this.w.objects.itemPositions()) {
          if (it.state !== 'idle') continue;
          const d = dist2D(s.x, s.z, it.pos.x, it.pos.z);
          if (d < 1.9 && Math.abs(s.y - (it.pos.y - 0.8)) < 2.2) consider(d, 'Coger célula de energía');
        }
      const missionActive = ui.get().mission?.status === 'active';
      for (const d of this.w.map.interactables) {
        const dd = dist2D(s.x, s.z, d.pos[0], d.pos[2]);
        if (dd >= d.radius || Math.abs(s.y - d.pos[1]) > 2.2) continue;
        if (d.kind === 'socket' && carrying) consider(dd, 'Insertar la célula en el generador');
        if (d.kind === 'valve' && missionActive) consider(dd, `Girar ${d.label.toLowerCase()}`);
        if (d.kind === 'lever' && missionActive) consider(dd, 'Tirar de la palanca');
        if (d.kind === 'redButton') consider(dd, 'Pulsar el botón rojo (¿qué podría salir mal?)');
        if (d.kind === 'vanishSwitch') consider(dd, 'Hacer desaparecer el puente');
      }
      if (!prompt && carrying) prompt = 'Soltar la célula';
    }
    if (prompt !== this.lastPrompt) {
      this.lastPrompt = prompt;
      ui.set({ prompt });
    }
  }

  private updateWaypoints() {
    const wps: Waypoint[] = [];
    const m = ui.get().mission;
    if (m?.status === 'active') {
      const obj = m.objectives[m.stageIndex];
      const carrying = !!(this.myFx & FX.CARRYING);
      const inter = (id: string) => this.w.map.interactables.find((i) => i.id === id)!;
      if (obj?.id === 'cells' || m.objectives[0].status !== 'done') {
        for (const it of this.w.objects.itemPositions()) if (it.state === 'idle') wps.push({ x: it.pos.x, y: it.pos.y + 1, z: it.pos.z, label: 'Célula', icon: '🔋' });
        if (carrying) {
          const s = inter('socket');
          wps.push({ x: s.pos[0], y: 2, z: s.pos[2], label: 'Generador', icon: '⚡' });
        }
      }
      if (obj?.id === 'plates') for (const id of ['plate-west', 'plate-east']) wps.push({ x: inter(id).pos[0], y: 1, z: inter(id).pos[2], label: 'Placa', icon: '⭕' });
      if (obj?.id === 'calibrate') {
        const d = inter('display');
        wps.push({ x: d.pos[0], y: d.pos[1] + 2, z: d.pos[2], label: 'Pantalla', icon: '📺' });
        for (const id of ['valve-1', 'valve-2', 'valve-3']) wps.push({ x: inter(id).pos[0], y: 2, z: inter(id).pos[2], label: 'Válvula', icon: '🔧' });
      }
      if (obj?.id === 'start') {
        const l = inter('lever');
        wps.push({ x: l.pos[0], y: 2, z: l.pos[2], label: 'Palanca', icon: '🕹️' });
      }
    }
    live.waypoints = wps;
  }

  /** Nombre del evento de mundo activo (para el HUD). */
  static eventLabel(m: WorldModifiers | null) {
    if (!m?.label) return null;
    const kind = (Object.keys(RANDOM_EVENT_INFO) as Array<keyof typeof RANDOM_EVENT_INFO>).find((k) => RANDOM_EVENT_INFO[k].name === m.label);
    return { text: m.label, icon: kind ? RANDOM_EVENT_INFO[kind].icon : '🚨', until: m.until };
  }

  dispose() {
    this.offs.forEach((f) => f());
    this.offs = [];
    for (const r of [this.local, ...this.remotes.values()]) {
      this.w.dynamic.remove(r.avatar.root);
      r.avatar.dispose();
    }
    this.remotes.clear();
    live.waypoints = [];
    this.w.engine.shadowTarget = null;
    this.w.setBlackout(false);
    this.w.setAlarm(false);
    void this.localCharacter;
  }
}
